import * as React from 'react';
import { colors } from '@workday/canvas-kit-react/tokens';
import { useTheme, createComponent, focusRing, } from '@workday/canvas-kit-react/common';
import { BaseButton, getMinWidthStyles, getPaddingStyles } from './BaseButton';
/**
 * Secondary Buttons have a medium level of emphasis. Use them for non-critical actions. Secondary
 * Buttons can be used on most pages without restrictions and work well for multiple actions of
 * equal weight. They can be used in conjunction with a Primary Button or independently.
 *
 * Secondary Buttons have four sizes: `extraSmall`, `small`, `medium`, and `large`. Icons are
 * supported for every size and can be positioned at the `start` or `end` with the `iconPosition`
 * prop.
 */
export const SecondaryButton = createComponent('button')({
    displayName: 'SecondaryButton',
    Component: ({ size = 'medium', iconPosition = 'start', variant, icon, shouldMirrorIcon = false, children, ...elemProps }, ref, Element) => {
        const theme = useTheme();
        return (React.createElement(BaseButton, Object.assign({ ref: ref, size: size, colors: getSecondaryButtonColors(variant, theme), padding: getPaddingStyles(children, size, icon, iconPosition), minWidth: getMinWidthStyles(children, size), as: Element }, elemProps),
            icon && iconPosition === 'start' && (React.createElement(BaseButton.Icon, { size: size, iconPosition: iconPosition, icon: icon, shouldMirrorIcon: shouldMirrorIcon })),
            children && React.createElement(BaseButton.Label, null, children),
            icon && iconPosition === 'end' && (React.createElement(BaseButton.Icon, { size: size, iconPosition: iconPosition, icon: icon, shouldMirrorIcon: shouldMirrorIcon }))));
    },
});
export const getSecondaryButtonColors = (variant, theme) => {
    const { canvas: { palette: { primary: themePrimary }, }, } = theme;
    switch (variant) {
        case undefined:
        default:
            return {
                default: {
                    background: 'transparent',
                    border: colors.blackPepper400,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                },
                hover: {
                    background: colors.blackPepper400,
                    border: colors.blackPepper400,
                    icon: themePrimary.contrast,
                    label: themePrimary.contrast,
                },
                active: {
                    background: colors.blackPepper500,
                    border: colors.blackPepper500,
                    icon: themePrimary.contrast,
                    label: themePrimary.contrast,
                },
                focus: {
                    border: colors.blackPepper400,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                },
                // Identical to 'default' styles. ButtonContainer will set opacity to 40%
                disabled: {
                    background: 'transparent',
                    border: colors.blackPepper400,
                    icon: colors.blackPepper400,
                    label: colors.blackPepper400,
                },
            };
        case 'inverse':
            return {
                default: {
                    background: 'transparent',
                    border: colors.frenchVanilla100,
                    icon: colors.frenchVanilla100,
                    label: colors.frenchVanilla100,
                },
                hover: {
                    background: colors.soap300,
                    border: colors.soap300,
                    icon: colors.blackPepper500,
                    label: colors.blackPepper500,
                },
                active: {
                    background: colors.soap400,
                    border: colors.soap400,
                    icon: colors.blackPepper500,
                    label: colors.blackPepper500,
                },
                focus: {
                    background: colors.frenchVanilla100,
                    icon: colors.blackPepper500,
                    label: colors.blackPepper500,
                    focusRing: focusRing({
                        separation: 2,
                        innerColor: 'currentColor',
                        outerColor: colors.frenchVanilla100,
                    }, theme),
                },
                // Identical to inverse 'default' styles. ButtonContainer will set opacity to 40%
                disabled: {
                    background: 'transparent',
                    border: colors.frenchVanilla100,
                    icon: colors.frenchVanilla100,
                    label: colors.frenchVanilla100,
                },
            };
    }
};
